import React, { useMemo } from "react";

import ActorAvatar from "components/Actors/ActorAvatar";
import { ACTOR_AVATAR_SIZES } from "util/actorAvatarSizes";
import type { Actor } from "types/actor";

export type UserStoryAvatarSize = "sm" | "md";

/**
 * Shape matching the backend's
 * `GET /api/entities/placements/users-active` per-owner row. Defined
 * inline here so callers can hand the raw API entry straight through
 * without re-mapping.
 */
export interface UserStoryRailEntry {
  user_id: number;
  username: string;
  display_name: string | null;
  /**
   * `properties_public` of the owner user. Only `avatar_key` is read;
   * `avatar_url` is intentionally ignored — the canonical
   * `<ActorAvatar>` always composes the displayable URL from
   * `IMAGE_CDN + avatar_key`.
   */
  properties_public: Record<string, unknown> | null | undefined;
  has_unseen: boolean;
}

export interface UserStoryAvatarProps {
  /** Owner row returned by the placements-active-users endpoint. */
  entry: UserStoryRailEntry;
  /** Visual size — `sm` for the carousel rail, `md` for the profile plate. */
  size?: UserStoryAvatarSize;
  /**
   * Optional override for the label shown under the avatar. Defaults
   * to the owner's display name (or username). Pass `null` to omit
   * the label entirely.
   */
  label?: string | null;
  /** Optional extra classes applied to the outermost wrapper. */
  className?: string;
}

const SIZE_PX: Record<UserStoryAvatarSize, number> = {
  sm: ACTOR_AVATAR_SIZES.rail,
  md: ACTOR_AVATAR_SIZES.profile,
};

/**
 * Backwards-compatible Instagram-style story avatar for the home rail
 * and other story-list surfaces.
 *
 * Now a thin adapter over the canonical `<ActorAvatar>`: it handles
 * the rail-specific concerns (label below, sizing convention) and
 * delegates ring + image + click semantics to the shared component.
 *
 * Prefer `<ActorAvatar>` directly for new code.
 */
const UserStoryAvatar: React.FC<UserStoryAvatarProps> = ({
  entry,
  size = "sm",
  label,
  className,
}) => {
  const actor = useMemo<Actor>(
    () => ({
      id: entry.user_id,
      kind: "user",
      username: entry.username,
      displayName: entry.display_name,
      avatarKey: (() => {
        const raw = entry.properties_public?.avatar_key;
        return typeof raw === "string" && raw.trim().length > 0 ? raw.trim() : null;
      })(),
    }),
    [entry]
  );

  const ownerLabel = entry.display_name || entry.username;
  const resolvedLabel = label === undefined ? ownerLabel : label;
  const labelClass =
    size === "sm"
      ? "max-w-[64px] truncate text-center text-[11px] text-eoe-text dark:text-eoe-dark-text"
      : "max-w-[72px] truncate text-center text-xs text-eoe-text dark:text-eoe-dark-text";

  return (
    <span
      className={`group flex shrink-0 flex-col items-center gap-1 ${className ?? ""}`}
      data-testid="user-story-avatar"
      data-has-unseen={entry.has_unseen ? "true" : "false"}
    >
      <ActorAvatar actor={actor} size={SIZE_PX[size]} />
      {resolvedLabel ? (
        <span className={labelClass}>{resolvedLabel}</span>
      ) : null}
    </span>
  );
};

export default UserStoryAvatar;
